/**
 * useDeleteSecret Hook
 *
 * Custom React Query mutation hook for deleting secrets.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { secretService } from '../../../api/secret.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseDeleteSecretOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for deleting secret
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useDeleteSecret = (tenantId: string, options?: UseDeleteSecretOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (secretId: string) => secretService.deleteSecret(tenantId, secretId),
    onSuccess: () => {
      // Invalidate secret list query
      queryClient.invalidateQueries({ queryKey: ['secrets', tenantId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
